/**
 * FolderListToolbar · 文件夹列表操作栏（宫格/列表共用）
 *
 * 连接型、零 props：自取 Redux 选择/视图状态并自派发，批量操作走
 * useFolderBatchActions()。左簇 = 全选 + 已选 N 项 +（多选态）批量按钮；
 * 右簇 = 配额 + 搜索框 + 排序 + 视图切换。容器带 data-no-box-select
 * 防止点工具栏起框选。
 */
import React, { useMemo } from 'react'
import classNames from 'classnames'
import { Button, Tooltip } from 'antd'
import {
  AppstoreOutlined,
  BarsOutlined,
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FolderOutlined
} from '@ant-design/icons'
import {
  useAppDispatch,
  useAppSelector,
  useIsAdmin
} from '../../../../app/hooks'
import {
  selectManageMode,
  selectManageSelectedFolderIds,
  selectViewMode,
  selectFolderListItems,
  enterManageMode,
  exitManageMode,
  setManageFolderSelection,
  setViewMode
} from '../../../../slices/aiClassify'
import { trackClassifyClick } from '../../utils/trackClassify'
import FolderSortMenu from '../FolderSortMenu'
import { useFolderBatchActions } from './FolderBatchActions'
import FolderQuotaWidget from './FolderQuotaWidget'
import FolderSearchInput from './FolderSearchInput'
import styles from './FolderListToolbar.module.scss'

const FolderListToolbar: React.FC = () => {
  const dispatch = useAppDispatch()
  const isAdmin = useIsAdmin()
  const manageMode = useAppSelector(selectManageMode)
  const selectedFolderIds = useAppSelector(selectManageSelectedFolderIds)
  const viewMode = useAppSelector(selectViewMode)
  const folders = useAppSelector(selectFolderListItems)
  const { batchDownload, batchMove, batchDelete, downloading, deleting } =
    useFolderBatchActions()

  /** 当前是否所有卡片都被选中 · 决定 √ / indeterminate / 空 三态与点击行为 */
  const allSelected = useMemo(
    () =>
      folders.length > 0 &&
      folders.every((f) => selectedFolderIds.indexOf(f.folderId) >= 0),
    [folders, selectedFolderIds]
  )
  const indeterminate =
    manageMode && selectedFolderIds.length > 0 && !allSelected

  /**
   * 全选 / 取消全选切换
   *  · 非多选：进入多选 + 全选
   *  · 多选 + 已全选：取消全选（清空，selection 清空会触发自动退出多选）
   *  · 多选 + 未全选：补齐全选
   */
  const handleToggleSelectAll = (): void => {
    if (!manageMode) {
      trackClassifyClick('selectAll')
      dispatch(enterManageMode())
      dispatch(setManageFolderSelection(folders.map((f) => f.folderId)))
      return
    }
    if (allSelected) {
      trackClassifyClick('unSelectAll')
      dispatch(setManageFolderSelection([]))
    } else {
      trackClassifyClick('selectAll')
      dispatch(setManageFolderSelection(folders.map((f) => f.folderId)))
    }
  }

  const hasSelection = selectedFolderIds.length > 0

  return (
    <div
      className={styles.selectionBar}
      role="toolbar"
      aria-label="文件夹列表工具栏"
      data-no-box-select
    >
      <div className={styles.selectionBarLeft}>
        <span
          className={styles.selectionCheckbox}
          onClick={handleToggleSelectAll}
          role="checkbox"
          aria-checked={indeterminate ? 'mixed' : allSelected}
          aria-label={allSelected ? '取消全选' : '全选'}
        >
          <span
            className={classNames(styles.selectionIndicator, {
              [styles.selectionIndicatorChecked]: allSelected,
              [styles.selectionIndicatorIndeterminate]:
                indeterminate && !allSelected
            })}
          >
            {allSelected && (
              <CheckOutlined style={{ fontSize: 12, color: '#fff' }} />
            )}
            {indeterminate && !allSelected && (
              <span className={styles.indeterminateBar} />
            )}
          </span>
          <span className={styles.selectionLabel}>全选</span>
        </span>

        {manageMode && hasSelection && (
          <span className={styles.selectionCount}>
            已选 <strong>{selectedFolderIds.length}</strong> 项
          </span>
        )}

        {manageMode && (
          <span className={styles.batchActions}>
            <Button
              type="primary"
              ghost
              icon={<DownloadOutlined />}
              disabled={!hasSelection || downloading}
              loading={downloading}
              onClick={batchDownload}
            >
              下载
            </Button>
            {isAdmin && (
              <Button
                icon={<FolderOutlined />}
                disabled={!hasSelection}
                onClick={batchMove}
              >
                移动到
              </Button>
            )}
            {isAdmin && (
              <Button
                danger
                ghost
                icon={<DeleteOutlined />}
                disabled={!hasSelection || deleting}
                loading={deleting}
                onClick={batchDelete}
              >
                删除
              </Button>
            )}
            <Button
              type="link"
              className={styles.cancelBtn}
              onClick={() => {
                trackClassifyClick('cancel')
                dispatch(exitManageMode())
              }}
            >
              取消
            </Button>
          </span>
        )}
      </div>

      <div className={styles.selectionBarRight}>
        <FolderQuotaWidget />
        {/* 搜索框 · 多选态时组件内部动画收起 */}
        <FolderSearchInput />
        <FolderSortMenu className={styles.toolbarBtn} />
        <Tooltip
          title={viewMode === 'grid' ? '切换为列表视图' : '切换为宫格视图'}
          placement="bottom"
        >
          <span className={styles.viewToggle}>
            <Button.Group>
              <Button
                type={viewMode === 'grid' ? 'primary' : 'default'}
                ghost={viewMode === 'grid'}
                icon={<AppstoreOutlined />}
                onClick={() => dispatch(setViewMode('grid'))}
                aria-label="宫格视图"
              />
              <Button
                type={viewMode === 'list' ? 'primary' : 'default'}
                ghost={viewMode === 'list'}
                icon={<BarsOutlined />}
                onClick={() => dispatch(setViewMode('list'))}
                aria-label="列表视图"
              />
            </Button.Group>
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

export default FolderListToolbar
